import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getProProfile, updateProProfile } from "../api";

interface Template {
  id: string;
  name: string;
  type: "message" | "quote";
  service: string;
  isPublic: boolean;
  body: string;
  lastEdited: string;
}

const VARIABLES = [
  { key: "{client_name}", label: "Client Name", example: "John" },
  { key: "{service_type}", label: "Service Type", example: "Moving" },
  { key: "{job_date}", label: "Job Date", example: "May 5" },
  { key: "{pickup_address}", label: "Pickup Address", example: "123 Main St" },
  { key: "{destination_address}", label: "Destination Address", example: "555 Pine St" },
  { key: "{job_price}", label: "Job Price", example: "$250" },
  { key: "{company_name}", label: "Your Business", example: "Pro Movers" },
  { key: "{your_name}", label: "Your Name", example: "Marcus" },
  { key: "{phone}", label: "Your Phone", example: "(713) 555-0100" },
];

const SAMPLES = {
  message_initial: `Hi {client_name},

Thanks for requesting {service_type} on {job_date}.

Were available and would love to help! Heres a quick estimate based on your request.

Please let me know if you have any questions

— {company_name}`,
  message_followup: `Hi {client_name},

Just following up on my quote for {service_type} on {job_date}. Let me know if you have any questions or are ready to book!

— {company_name}`,
  message_thanks: `Hi {client_name},

Thank you for choosing {company_name} for your {service_type}! It was great working with you. If youre satisfied with our service, wed appreciate a quick review.

— {your_name}`,
  quote_moving: `Hello {client_name},

Here is your moving quote:

Service: {service_type}
Date: {job_date}

Pickup: {pickup_address}
Drop-off: {destination_address}

Crew: 2 Movers + Truck
Estimated Time: 3 hours

Total Estimate: {job_price}

This includes:
- Truck and fuel
- Moving equipment
- Furniture protection

Let me know if youd like to confirm your booking!

— {company_name}`,
  quote_cleaning: `Hello {client_name},

Heres your cleaning quote:

Service: {service_type}
Date: {job_date}
Address: {pickup_address}

Total Estimate: {job_price}

Includes:
- All cleaning supplies provided
- Eco-friendly products available on request
- Satisfaction guarantee

Let me know if youd like to schedule!

— {company_name}`,
  quote_general: `Hi {client_name},

Thanks for the inquiry. Heres my quote:

Service: {service_type}
Job Date: {job_date}
Total: {job_price}

Terms:
- 50% deposit to secure booking
- Balance due upon completion
- 1-year workmanship warranty

Available payment: cash, card, Venmo

Ready to book? Reply YES to confirm.

— {company_name}`
};

function newTemplate(type: "message" | "quote" = "message", presetKey: string = ""): Template {
  const presets: any = {
    "message_initial": { name: "Initial Response", body: SAMPLES.message_initial },
    "message_followup": { name: "Follow-Up", body: SAMPLES.message_followup },
    "message_thanks": { name: "Thank You / Review Request", body: SAMPLES.message_thanks },
    "quote_moving": { name: "Moving Quote", body: SAMPLES.quote_moving },
    "quote_cleaning": { name: "Cleaning Quote", body: SAMPLES.quote_cleaning },
    "quote_general": { name: "General Service Quote", body: SAMPLES.quote_general },
  };
  const preset = presets[presetKey] || { name: type === "quote" ? "New Quote" : "New Message", body: "Hi {client_name},\n\n\n\n— {company_name}" };
  return {
    id: "tpl_" + Date.now(),
    name: preset.name,
    type,
    service: "All Services",
    isPublic: false,
    body: preset.body,
    lastEdited: new Date().toISOString()
  };
}

export default function TemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"all" | "message" | "quote">("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showVariables, setShowVariables] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const p = await getProProfile();
        setProfile(p);

        // Load existing templates - migrate old quoteTemplates if needed
        let tpls: Template[] = p.messageTemplates || [];
        if (tpls.length === 0 && p.quoteTemplates && p.quoteTemplates.length > 0) {
          tpls = p.quoteTemplates.map((q: any) => ({
            id: q.id || "tpl_" + Date.now(),
            name: q.name || "Quote",
            type: "quote" as const,
            service: q.service || "All Services",
            isPublic: q.isPublic || false,
            body: q.intro + "\n\n" + (q.scope || "") + "\n\nTerms:\n• " + q.deposit + "\n• " + q.timeline + "\n• " + q.warranty + "\n• " + q.payment + "\n\n" + q.closing,
            lastEdited: new Date().toISOString()
          }));
        }
        setTemplates(tpls);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  function addTemplate(type: "message" | "quote", preset: string = "") {
    const tpl = newTemplate(type, preset);
    setTemplates(prev => [...prev, tpl]);
    setEditingId(tpl.id);
    setShowAddMenu(false);
  }

  function duplicateTemplate(id: string) {
    const orig = templates.find(t => t.id === id);
    if (!orig) return;
    const copy = { ...orig, id: "tpl_" + Date.now(), name: orig.name + " (Copy)", lastEdited: new Date().toISOString() };
    setTemplates(prev => [...prev, copy]);
  }

  function deleteTemplate(id: string) {
    if (!window.confirm("Delete this template?")) return;
    setTemplates(prev => prev.filter(t => t.id !== id));
  }

  function updateTemplate(id: string, key: keyof Template, value: any) {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, [key]: value, lastEdited: new Date().toISOString() } : t));
  }

  function insertVariable(id: string, variable: string) {
    const tpl = templates.find(t => t.id === id);
    if (!tpl) return;
    const textarea = document.getElementById("body_" + id) as HTMLTextAreaElement;
    if (!textarea) {
      updateTemplate(id, "body", tpl.body + " " + variable);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = tpl.body.slice(0, start) + variable + tpl.body.slice(end);
    updateTemplate(id, "body", newBody);
    setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + variable.length, start + variable.length); }, 0);
  }

  async function saveAll() {
    setSaving(true);
    try {
      await updateProProfile({ ...profile, messageTemplates: templates });
      alert("Saved!");
    } catch (e) { console.error(e); alert("Failed to save"); }
    finally { setSaving(false); }
  }

  function preview(body: string) {
    return body
      .replace(/\{client_name\}/g, "John")
      .replace(/\{service_type\}/g, "House Cleaning")
      .replace(/\{job_date\}/g, "May 5")
      .replace(/\{pickup_address\}/g, "123 Main St")
      .replace(/\{destination_address\}/g, "555 Pine St")
      .replace(/\{job_price\}/g, "$250")
      .replace(/\{company_name\}/g, profile?.businessName || profile?.name || "Your Business")
      .replace(/\{your_name\}/g, profile?.name || "You")
      .replace(/\{phone\}/g, profile?.phone || "(713) 555-0100");
  }

  const filtered = templates.filter(t => activeTab === "all" || t.type === activeTab);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-5 pb-0 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigate("/home")} className="text-gray-400 text-sm font-semibold">← Dashboard</button>
          <button onClick={saveAll} disabled={saving}
            className="bg-mouna-blue text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">
            {saving ? "Saving..." : "Save All"}
          </button>
        </div>
        <h1 className="font-display text-2xl font-bold text-navy mb-1">Templates</h1>
        <p className="text-sm text-gray-400 mb-3">Saved replies and quote templates with auto-fill</p>

        <div className="flex gap-1 -mx-5 px-5">
          {[["all", "All (" + templates.length + ")"], ["message", "💬 Messages"], ["quote", "💰 Quotes"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key as any)}
              className={"py-3 px-4 text-xs font-bold transition-all relative " + (activeTab === key ? "text-mouna-blue" : "text-gray-400")}>
              {label}
              {activeTab === key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-mouna-blue rounded-full"/>}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* Add Template Menu */}
        <div className="relative mb-4">
          <button onClick={() => setShowAddMenu(!showAddMenu)}
            className="w-full bg-mouna-blue text-white rounded-2xl py-3 font-bold text-sm flex items-center justify-center gap-2">
            + Create New Template
          </button>

          {showAddMenu && (
            <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-xl z-30 p-3 border border-gray-100">
              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 px-1">Message Templates</div>
              <button onClick={() => addTemplate("message", "message_initial")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">💬 Initial Response</button>
              <button onClick={() => addTemplate("message", "message_followup")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">📩 Follow-Up</button>
              <button onClick={() => addTemplate("message", "message_thanks")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">⭐ Thank You / Review Request</button>
              <button onClick={() => addTemplate("message")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-gray-400">+ Blank Message</button>

              <div className="border-t border-gray-100 my-2"></div>

              <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2 px-1">Quote Templates</div>
              <button onClick={() => addTemplate("quote", "quote_moving")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">🚚 Moving Quote</button>
              <button onClick={() => addTemplate("quote", "quote_cleaning")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">🧹 Cleaning Quote</button>
              <button onClick={() => addTemplate("quote", "quote_general")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-navy">💼 General Service Quote</button>
              <button onClick={() => addTemplate("quote")} className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-pale text-sm text-gray-400">+ Blank Quote</button>
            </div>
          )}
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-card">
            <div className="text-4xl mb-3">📝</div>
            <h2 className="font-bold text-navy mb-2">No templates yet</h2>
            <p className="text-sm text-gray-400 mb-4">Create templates to send professional quotes and messages in seconds.</p>
          </div>
        )}

        {/* Templates list */}
        {filtered.map(tpl => {
          const isEditing = editingId === tpl.id;
          return (
            <div key={tpl.id} className="bg-white rounded-2xl p-4 shadow-card mb-3">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-blue-pale text-mouna-blue">
                      {tpl.type === "quote" ? "💰 Quote" : "💬 Message"}
                    </span>
                    {tpl.isPublic && <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-teal-light text-teal">👁 Public</span>}
                  </div>
                  {isEditing ? (
                    <input type="text" value={tpl.name} onChange={e => updateTemplate(tpl.id, "name", e.target.value)}
                      className="w-full font-bold text-navy outline-none border-b border-gray-200 pb-1 focus:border-mouna-blue"/>
                  ) : (
                    <h3 className="font-bold text-navy">{tpl.name}</h3>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => setEditingId(isEditing ? null : tpl.id)}
                    className="bg-blue-pale text-mouna-blue text-xs font-bold rounded-full px-3 py-1.5">
                    {isEditing ? "Done" : "Edit"}
                  </button>
                  <button onClick={() => duplicateTemplate(tpl.id)}
                    className="bg-gray-50 text-gray-600 text-xs font-bold rounded-full px-3 py-1.5">📋</button>
                  <button onClick={() => deleteTemplate(tpl.id)}
                    className="bg-red-50 text-red-500 text-xs font-bold rounded-full px-2 py-1.5">✕</button>
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-3 mt-3 border-t border-gray-100 pt-3">
                  <div className="flex gap-2">
                    <button onClick={() => updateTemplate(tpl.id, "type", tpl.type === "quote" ? "message" : "quote")}
                      className="flex-1 bg-gray-50 text-gray-600 text-xs font-bold rounded-full py-2">
                      Switch to {tpl.type === "quote" ? "Message" : "Quote"}
                    </button>
                    <button onClick={() => updateTemplate(tpl.id, "isPublic", !tpl.isPublic)}
                      className={"flex-1 text-xs font-bold rounded-full py-2 " + (tpl.isPublic ? "bg-teal-light text-teal" : "bg-gray-50 text-gray-500")}>
                      {tpl.isPublic ? "👁 Visible to clients" : "🔒 Private"}
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Template Body</label>
                    <button onClick={() => setShowVariables(showVariables)}
                      className="text-xs text-mouna-blue font-bold">+ Insert Variable ▾</button>
                  </div>

                  <div className="bg-blue-pale rounded-xl p-3">
                    <div className="text-xs text-mouna-blue font-bold mb-2">Click to insert:</div>
                    <div className="flex flex-wrap gap-1.5">
                      {VARIABLES.map(v => (
                        <button key={v.key} onClick={() => insertVariable(tpl.id, v.key)}
                          className="bg-white text-mouna-blue text-xs font-bold rounded-full px-2.5 py-1 hover:bg-mouna-blue hover:text-white transition-colors">
                          {v.key}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea id={"body_" + tpl.id} value={tpl.body} onChange={e => updateTemplate(tpl.id, "body", e.target.value)}
                    rows={10} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-mouna-blue resize-y font-mono"/>

                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">Live Preview</div>
                    <div className="text-xs text-gray-600 whitespace-pre-line leading-relaxed">{preview(tpl.body)}</div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 line-clamp-3 mt-2 whitespace-pre-line">{tpl.body.slice(0, 200)}{tpl.body.length > 200 && "..."}</div>
              )}

              <div className="text-xs text-gray-400 mt-2">Last edited: {new Date(tpl.lastEdited).toLocaleDateString()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
